import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  IconButton,
  Grid,
  Autocomplete,
  InputAdornment,
  List,
  ListItem,
  CircularProgress,
  Stack,
  debounce,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  CalendarMonth as CalendarMonthIcon,
} from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useNavigate } from "react-router-dom";
import NewCustomerDialog from "../../workOrder/customer/NewCustomerDialog";

// Simple unique ID generator (alternative to uuid)
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import {
  ScheduleItem,
  CreateScheduleItemRequest,
  UpdateScheduleItemRequest,
  SegmentFormData,
} from "../types/scheduleItem.types";
import {
  formSegmentsNeedIdSync,
  mergeSegmentIdsIntoFormSegments,
} from "../utils/workOrderSegmentSync";
import { formatDate, toLocalISOString } from "../utils/timeUtils";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import { CustomerAPI } from "../../QueueBoard/api/customerApi";
import { UnitsAPI } from "../../QueueBoard/api/unitApi";
import { LookupAPI } from "../../QueueBoard/api/lookupDataApi";
import localStorageHelper from "../../../app/localStorage";
import { getAuditUserName } from "../../common/Utils/auditUser";
import ScheduleItemAPI from "../api/scheduleItemApi";
import {
  searchCustomerListStyleTechnicianCalendar,
  searchUnitListStyle,
  searchListItem,
  ButtonStyle,
  ButtonStyleOutlined,
} from "../../QueueBoard/models/workOrderStyles";
import { Unit } from "../../QueueBoard/models/workOrderTypes";

interface ScheduleItemDialogProps {
  open: boolean;
  onClose: () => void;
  item?: ScheduleItem | null;
  initialDate?: Date;
  initialTime?: Date;
  locations: { locationId: number; locationName: string }[];
  currentUserId: number;
  onSave: (
    request: CreateScheduleItemRequest | UpdateScheduleItemRequest,
  ) => Promise<void>;
  onDelete?: (scheduleItemId: number) => void;
  onCreateWorkOrder?: (workOrderData: WorkOrderFormData) => void;
  /** True while a work order create/update operation is in flight. Disables WO action buttons to prevent duplicate submissions. */
  isWorkOrderOperationPending?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Re-export for consumers that import from this module
export type { SegmentFormData };

// Work Order Form Data interface
export interface WorkOrderFormData {
  customerIdentity: number;
  locationIdentity: number;
  unitIdentity?: number;
  woStatusIdentity: number;
  woPriorityIdentity: number;
  salesPersonIdentity: number;
  technicianIdentity?: number;
  // Single segment fields for backward compatibility
  segmentTitle: string;
  serviceCodeIdentity: number;
  // Multiple segments support
  segments: SegmentFormData[];
  actionType?: "draft" | "commit" | "save" | "archive";
  workOrderId?: number;
  workOrderNumber?: string;
  workOrderStatus?: "draft" | "committed";
  scheduleItemId?: number;
  segmentId?: number;
}

// Bill As options
const billAsOptions = [
  { key: "actual", label: "Actual" },
  { key: "flatrate", label: "Flat Rate" },
] as const;

// Customer option type
interface CustomerOption {
  identity: number;
  name: string;
  number: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const statusOptions = [
  "Scheduled",
  "Confirmed",
  "Completed",
  "Cancelled",
] as const;

// Helper to format Date to datetime-local string (YYYY-MM-DDTHH:mm)
const toDateTimeLocalString = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Helper to parse datetime-local string to Date
const fromDateTimeLocalString = (value: string): Date => {
  return new Date(value);
};

// Helper for required field label
const RequiredLabel: React.FC<{ label: string }> = ({ label }) => (
  <span>
    {label} <span style={{ color: "red" }}>*</span>
  </span>
);

export const ScheduleItemDialog: React.FC<ScheduleItemDialogProps> = ({
  open,
  onClose,
  item,
  initialDate,
  initialTime,
  locations,
  currentUserId,
  onSave,
  onDelete,
  onCreateWorkOrder,
  isWorkOrderOperationPending = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!item;
  const [activeTab, setActiveTab] = useState(0);

  // Get user info from localStorage
  const user = useMemo(() => localStorageHelper.getByKey("user"), []);
  const userName = useMemo(() => getAuditUserName(), []);

  // Get lookup data from zustand store
  const {
    locations: woLocations,
    woStatuses,
    woPriorityList,
    salesPersons,
    technicians,
    serviceCodes,
    setLocationLookup,
  } = useWorkOrderStore();

  // Schedule Item form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState<number>(0);
  const [customerName, setCustomerName] = useState("");
  const [locationId, setLocationId] = useState<number>(0);
  const [callInDateTime, setCallInDateTime] = useState<Date | null>(null);
  const [callInDateTimeString, setCallInDateTimeString] = useState("");
  const [startDateTime, setStartDateTime] = useState<Date>(new Date());
  const [endDateTime, setEndDateTime] = useState<Date>(new Date());
  const [startDateTimeString, setStartDateTimeString] = useState("");
  const [endDateTimeString, setEndDateTimeString] = useState("");
  const [status, setStatus] =
    useState<(typeof statusOptions)[number]>("Scheduled");
  const [timeZoneId, setTimeZoneId] = useState("Eastern Standard Time");

  // Helper to create a new empty segment
  const createEmptySegment = (): SegmentFormData => ({
    id: generateId(),
    segmentId: 0, // 0 signals "new segment, not yet persisted"
    segmentTitle: "",
    serviceCodeIdentity: 0,
    billAs: "actual",
    estimatedHours: 0,
  });

  // Work Order form state
  const [workOrderData, setWorkOrderData] = useState<WorkOrderFormData>({
    customerIdentity: 0,
    locationIdentity: 0,
    woStatusIdentity: 0,
    woPriorityIdentity: 0,
    salesPersonIdentity: 0,
    technicianIdentity: undefined,
    segmentTitle: "",
    serviceCodeIdentity: 0,
    segments: [createEmptySegment()], // Start with one empty segment
  });

  // Track touched fields for validation display
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );

  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  // Track which segment's estimated-hours field is currently focused
  // so we can visually clear the default `0` while the user is editing it.
  const [focusedHoursSegmentId, setFocusedHoursSegmentId] = useState<
    string | null
  >(null);

  // Customer search state for Work Order tab
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const customerSearchRef = useRef<HTMLDivElement>(null);

  // Unit search state for Details tab
  const [units, setUnits] = useState<Unit[]>([]);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [unitSearchLoading, setUnitSearchLoading] = useState(false);
  const [unitLoadingMore, setUnitLoadingMore] = useState(false);
  const [unitHasMore, setUnitHasMore] = useState(false);
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const unitSearchRef = useRef<HTMLDivElement>(null);
  const unitSearchPageRef = useRef(1);
  const unitSearchContextRef = useRef<{
    searchTerm: string;
    locId: number;
    custId: number | null;
  } | null>(null);

  const UNIT_SEARCH_PAGE_SIZE = 10;

  // Customer dialog state
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerDialogMode, setCustomerDialogMode] = useState<"create" | "edit">("create");

  // Transform lookup data for dropdowns
  const locationOptions = useMemo(
    () =>
      woLocations.map((loc) => ({
        identity: loc.identity,
        displayText: loc.displayText,
      })),
    [woLocations],
  );

  const statusOptionsWO = useMemo(
    () =>
      woStatuses.map((status) => ({
        identity: status.identity,
        displayText: status.displayText,
      })),
    [woStatuses],
  );

  const priorityOptions = useMemo(
    () =>
      woPriorityList.map((priority) => ({
        identity: priority.identity,
        displayText: priority.displayText,
      })),
    [woPriorityList],
  );

  const salesPersonOptions = useMemo(
    () =>
      salesPersons.map((sp) => ({
        identity: sp.identity,
        displayText: sp.userName,
      })),
    [salesPersons],
  );

  const technicianOptions = useMemo(
    () =>
      technicians.map((tech) => ({
        identity: tech.identity,
        displayText: tech.userName,
      })),
    [technicians],
  );

  const serviceCodeOptions = useMemo(
    () =>
      serviceCodes.map((code) => ({
        identity: code.identity,
        code: code.serviceCode,
        description: code.description,
        displayText: `${code.serviceCode} - ${code.description}`,
      })),
    [serviceCodes],
  );

  // Helper functions to get default values for work order dropdowns
  const getDefaultSalesPersonIdentity = useCallback(() => {
    if (!user?.isSalesPerson) {
      return undefined;
    }
    const salesPerson = salesPersons.find(
      (person) => person.identity === user.identity,
    );
    return salesPerson ? salesPerson.identity : undefined;
  }, [user, salesPersons]);

  const getDefaultLocationIdentity = useCallback(() => {
    const location = woLocations.find(
      (loc) => loc.identity === user?.defaultLocationIdentity,
    );
    return location ? location.identity : undefined;
  }, [woLocations, user]);

  const getDefaultWoStatus = useCallback(() => {
    const statusView = JSON.parse(
      localStorage.getItem(`IsStatusView_${userName}`) || "false",
    );
    const statusIdentity = JSON.parse(
      localStorage.getItem("statusIdentity") || "null",
    );
    if (statusView && statusIdentity) {
      return (
        woStatuses.find((status) => status.identity === Number(statusIdentity))
          ?.identity || undefined
      );
    }
    return woStatuses.find((status) => status.isDefault)?.identity || undefined;
  }, [woStatuses, userName]);

  const getDefaultPriority = useCallback(() => {
    return (
      woPriorityList.find((priority) => priority.isDefault)?.identity ||
      undefined
    );
  }, [woPriorityList]);

  const getDefaultTechnicianIdentity = useCallback(() => {
    const isTechView = JSON.parse(
      localStorage.getItem(`IsTechView_${userName}`) || "false",
    );
    const techIdentity = JSON.parse(
      localStorage.getItem("techIdentity") || "null",
    );
    if (isTechView && techIdentity) {
      return (
        technicians?.find((tech) => tech.identity === Number(techIdentity))
          ?.identity || undefined
      );
    }
    return undefined;
  }, [technicians, userName]);

  // Track the previous item's scheduleItemId to detect when a NEW item is opened vs. same item updated
  const prevItemIdRef = useRef<number | null>(null);

  // Initialize schedule item form when dialog opens or item changes
  // IMPORTANT: We check both `open` and the item to ensure proper initialization
  useEffect(() => {
    // Reset the ref when dialog closes so reopening will reinitialize
    if (!open) {
      prevItemIdRef.current = null;
      return;
    }

    // Check if this is a NEW item being opened (different scheduleItemId)
    // vs. the same item being updated (e.g., after saving work order)
    const isNewItem = item?.scheduleItemId !== prevItemIdRef.current;
    
    // Update the ref for next comparison
    if (item) {
      prevItemIdRef.current = item.scheduleItemId;
    } else {
      prevItemIdRef.current = null;
    }

    // Only reset tabs and touched fields for new items
    if (isNewItem) {
      setActiveTab(0);
      setTouchedFields({});
    }

    if (item) {
      // Editing mode - always update basic schedule item fields
      setTitle(item.title);
      setDescription(item.description || "");
      setCustomerId(item.customerId);
      setCustomerName(item.customerName);
      setLocationId(item.locationId);
      if (item.callInDateTime && !Number.isNaN(item.callInDateTime.getTime())) {
        setCallInDateTime(item.callInDateTime);
        setCallInDateTimeString(toDateTimeLocalString(item.callInDateTime));
      } else {
        setCallInDateTime(null);
        setCallInDateTimeString("");
      }
      setStartDateTime(item.startDateTime);
      setEndDateTime(item.endDateTime);
      setStartDateTimeString(toDateTimeLocalString(item.startDateTime));
      setEndDateTimeString(toDateTimeLocalString(item.endDateTime));
      setStatus(item.status);
      setTimeZoneId(item.timeZoneId);

      // Only initialize work order form when opening a NEW item (not after save)
      // This prevents resetting user-entered values after saving draft/commit
      if (isNewItem) {
        // Pre-fill work order form with schedule item data
        // If work order is linked, use its values, otherwise use defaults
        const hasLinkedWorkOrder = !!item.workOrderId;

        const defaultLoc = getDefaultLocationIdentity();
        const defaultStatus = getDefaultWoStatus();
        const defaultPriority = getDefaultPriority();
        const defaultSalesPerson = getDefaultSalesPersonIdentity();
        const defaultTechnician = getDefaultTechnicianIdentity();

        // Build the segments list for the editor.
        // Priority:
        //   1. If the linked WO returned its full segment list from the API,
        //      bind all of them (preserves billAs / estimatedHours).
        //   2. Otherwise fall back to the single-segment fields (legacy path).
        //   3. For brand-new items with no WO, start with one empty row.
        const apiSegments = item.segments ?? [];
        let initialSegments: SegmentFormData[];

        if (hasLinkedWorkOrder && apiSegments.length > 0) {
          initialSegments = apiSegments.map((seg) => ({
            id: generateId(),
            // Preserve the backend InvoiceSegment identity so the update
            // endpoint can match each UI segment to the correct DB row.
            segmentId: seg.segmentId ?? 0,
            segmentTitle: seg.segmentTitle ?? "",
            serviceCodeIdentity: seg.serviceCodeId ?? 0,
            billAs: seg.billAs ?? "actual",
            estimatedHours: seg.estimatedHours ?? 0,
          }));
        } else {
          initialSegments = [
            {
              id: generateId(),
              // Fall back to the legacy single-segment id on the item, if any.
              segmentId: hasLinkedWorkOrder ? item.segmentId ?? 0 : 0,
              segmentTitle:
                hasLinkedWorkOrder && item.segmentTitle
                  ? item.segmentTitle
                  : item.title.slice(0, 15),
              serviceCodeIdentity:
                hasLinkedWorkOrder && item.serviceCodeId
                  ? item.serviceCodeId
                  : 0,
              billAs: "actual",
              estimatedHours: 0,
            },
          ];
        }

        // First segment drives the legacy single-segment fields so existing
        // validation / save paths keep working unchanged.
        const firstSegment = initialSegments[0];

        setWorkOrderData({
          customerIdentity: item.customerId,
          locationIdentity: defaultLoc || item.locationId,
          // Use linked work order values if available, otherwise defaults
          woStatusIdentity:
            hasLinkedWorkOrder && item.woStatusId
              ? item.woStatusId
              : defaultStatus || 0,
          woPriorityIdentity:
            hasLinkedWorkOrder && item.woPriorityId
              ? item.woPriorityId
              : defaultPriority || 0,
          salesPersonIdentity:
            hasLinkedWorkOrder && item.woSalesPersonId
              ? item.woSalesPersonId
              : defaultSalesPerson || 0,
          technicianIdentity:
            hasLinkedWorkOrder && item.woTechnicianId
              ? item.woTechnicianId
              : defaultTechnician,
          // Single segment fields for backward compatibility (first segment)
          segmentTitle: firstSegment.segmentTitle,
          serviceCodeIdentity: firstSegment.serviceCodeIdentity,
          // Multiple segments (full list when returned by API)
          segments: initialSegments,
        });
        setCustomerSearchTerm((item.customerName || '').trim());
      }
      // Populate unit if exists
      if (item.unitId && item.unitNumber) {
        const unitFromItem: Unit = {
          identity: item.unitId,
          unitNumber: item.unitNumber,
          baseSerial: "",
          make: "",
          model: "",
          description: item.unitDescription || "",
          meterType: "",
          meterValue: 0,
          warrantyDate: new Date(),
          stockstatus: "",
          tag: "",
          isAttachment: false,
          isNew: false,
        };
        setSelectedUnit(unitFromItem);
        setUnitSearchTerm(item.unitNumber);
      } else {
        setSelectedUnit(null);
        setUnitSearchTerm("");
      }
      setUnits([]);
    } else {
      // Creating mode - use initial date/time
      const baseDate = initialDate || new Date();
      const baseTime = initialTime || new Date();

      const start = new Date(baseDate);
      start.setHours(baseTime.getHours(), baseTime.getMinutes(), 0, 0);

      const end = new Date(start);
      end.setMinutes(end.getMinutes() + 60); // Default 1 hour duration

      setTitle("");
      setDescription("");
      setCustomerId(0);
      setCustomerName("");
      setLocationId(locations[0]?.locationId || 0);
      const callInNow = new Date();
      setCallInDateTime(callInNow);
      setCallInDateTimeString(toDateTimeLocalString(callInNow));
      setStartDateTime(start);
      setEndDateTime(end);
      setStartDateTimeString(toDateTimeLocalString(start));
      setEndDateTimeString(toDateTimeLocalString(end));
      setStatus("Scheduled");
      setTimeZoneId("Eastern Standard Time");

      // Reset work order form with defaults
      const defaultLoc = getDefaultLocationIdentity();
      const defaultStatus = getDefaultWoStatus();
      const defaultPriority = getDefaultPriority();
      const defaultSalesPerson = getDefaultSalesPersonIdentity();
      const defaultTechnician = getDefaultTechnicianIdentity();

      const emptySegment = createEmptySegment();
      setWorkOrderData({
        customerIdentity: 0,
        locationIdentity: defaultLoc || 0,
        woStatusIdentity: defaultStatus || 0,
        woPriorityIdentity: defaultPriority || 0,
        salesPersonIdentity: defaultSalesPerson || 0,
        technicianIdentity: defaultTechnician,
        segmentTitle: "",
        serviceCodeIdentity: 0,
        segments: [emptySegment],
      });
      setCustomerSearchTerm("");
      // Reset unit search
      setSelectedUnit(null);
      setUnitSearchTerm("");
      setUnits([]);
    }
  }, [
    open,
    item,
    initialDate,
    initialTime,
    locations,
    getDefaultSalesPersonIdentity,
    getDefaultLocationIdentity,
    getDefaultWoStatus,
    getDefaultPriority,
    getDefaultTechnicianIdentity,
  ]);

  // Set default values when lookup data becomes available (following smartWoCreationDialog pattern)
  useEffect(() => {
    if (!open) return;

    // Set default technician if not already set and technicians are available
    if (technicians.length > 0 && !workOrderData.technicianIdentity) {
      const defaultTechId = getDefaultTechnicianIdentity();
      if (defaultTechId) {
        setWorkOrderData((prev) => ({
          ...prev,
          technicianIdentity: defaultTechId,
        }));
      }
    }

    // Set default sales person if not already set
    if (salesPersons.length > 0 && !workOrderData.salesPersonIdentity) {
      const defaultSalesPerson = getDefaultSalesPersonIdentity();
      if (defaultSalesPerson) {
        setWorkOrderData((prev) => ({
          ...prev,
          salesPersonIdentity: defaultSalesPerson,
        }));
      }
    }

    // Set default status if not already set
    if (woStatuses.length > 0 && !workOrderData.woStatusIdentity) {
      const defaultStatus = getDefaultWoStatus();
      if (defaultStatus) {
        setWorkOrderData((prev) => ({
          ...prev,
          woStatusIdentity: defaultStatus,
        }));
      }
    }

    // Set default priority if not already set
    if (woPriorityList.length > 0 && !workOrderData.woPriorityIdentity) {
      const defaultPriority = getDefaultPriority();
      if (defaultPriority) {
        setWorkOrderData((prev) => ({
          ...prev,
          woPriorityIdentity: defaultPriority,
        }));
      }
    }

    // Set default location if not already set
    if (woLocations.length > 0 && !workOrderData.locationIdentity) {
      const defaultLocation = getDefaultLocationIdentity();
      if (defaultLocation) {
        setWorkOrderData((prev) => ({
          ...prev,
          locationIdentity: defaultLocation,
        }));
      }
    }
  }, [
    open,
    technicians,
    salesPersons,
    woStatuses,
    woPriorityList,
    woLocations,
    workOrderData.technicianIdentity,
    workOrderData.salesPersonIdentity,
    workOrderData.woStatusIdentity,
    workOrderData.woPriorityIdentity,
    workOrderData.locationIdentity,
    getDefaultTechnicianIdentity,
    getDefaultSalesPersonIdentity,
    getDefaultWoStatus,
    getDefaultPriority,
    getDefaultLocationIdentity,
  ]);

  // When a work order is linked and the API returns segment ids (e.g. after Save
  // Draft), merge them into local form state without resetting user edits.
  useEffect(() => {
    if (!open || !item?.workOrderId) {
      return;
    }

    const apiSegments = item.segments ?? [];
    const hasApiSegments =
      apiSegments.length > 0 || (item.segmentId ?? 0) > 0;

    if (!hasApiSegments || !formSegmentsNeedIdSync(workOrderData.segments, item)) {
      return;
    }

    const mergedSegments = mergeSegmentIdsIntoFormSegments(
      workOrderData.segments,
      apiSegments,
    );

    const idsChanged = mergedSegments.some(
      (seg, index) => seg.segmentId !== workOrderData.segments[index]?.segmentId,
    );
    if (!idsChanged) {
      return;
    }

    const firstSegment = mergedSegments[0];
    setWorkOrderData((prev) => ({
      ...prev,
      workOrderId: item.workOrderId,
      workOrderNumber: item.workOrderNumber ?? prev.workOrderNumber,
      workOrderStatus:
        item.woStatus?.toLowerCase() === "committed" ? "committed" : "draft",
      segments: mergedSegments,
      segmentTitle: firstSegment?.segmentTitle ?? prev.segmentTitle,
      serviceCodeIdentity:
        firstSegment?.serviceCodeIdentity ?? prev.serviceCodeIdentity,
    }));
  }, [
    open,
    item?.workOrderId,
    item?.workOrderNumber,
    item?.woStatus,
    item?.segments,
    item?.segmentId,
    workOrderData.segments,
  ]);

  // Fetch locations from API if store cache is empty
  useEffect(() => {
    if (woLocations.length === 0) {
      LookupAPI.getLocations().then((locations) => {
        if (locations.length > 0) {
          setLocationLookup(locations);
        }
      });
    }
  }, [woLocations.length, setLocationLookup]);

  // Reset customer and unit search when dialog closes
  useEffect(() => {
    if (!open) {
      setCustomers([]);
      setShowCustomerDropdown(false);
      setCustomerSearchTerm("");
      setUnits([]);
      setShowUnitDropdown(false);
      setUnitSearchTerm("");
      setSelectedUnit(null);
    }
  }, [open]);

  // Handle click outside customer search dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerSearchRef.current &&
        !customerSearchRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Debounced customer search
  const debouncedSearchCustomers = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (searchTerm.length < 2) {
          setCustomers([]);
          setShowCustomerDropdown(false);
          return;
        }

        setCustomerSearchLoading(true);
        try {
          const result = await CustomerAPI.searchCustomersByName(searchTerm);
          if (result) {
            setCustomers(
              result.map((customer) => ({
                identity: customer.identity,
                name: customer.name || "",
                number: customer.number || "",
              })),
            );
            setShowCustomerDropdown(true);
          } else {
            setCustomers([]);
            setShowCustomerDropdown(false);
          }
        } catch (error) {
          console.error("Error searching customers:", error);
          setCustomers([]);
        } finally {
          setCustomerSearchLoading(false);
        }
      }, 300),
    [],
  );

  // Handle customer search input change
  const handleCustomerSearchChange = (value: string) => {
    setCustomerSearchTerm(value);
    // Invalidate customer selection when user modifies the search term
    // This ensures user must select from the dropdown
    if (customerId > 0) {
      setCustomerId(0);
      setCustomerName("");
      // Clear unit when customer is invalidated
      setSelectedUnit(null);
      setUnitSearchTerm("");
      setUnits([]);
      // Clear work order customer data
      setWorkOrderData((prev) => ({
        ...prev,
        customerIdentity: 0,
      }));
    }
    debouncedSearchCustomers(value);
  };

  // Handle customer selection - also updates work order if linked
  const handleCustomerSelect = async (customer: CustomerOption) => {
    setCustomerId(customer.identity);
    setCustomerName(customer.name);
    setCustomerSearchTerm(`${customer.name} / ${customer.number}`.trim());
    setShowCustomerDropdown(false);
    setCustomers([]);
    // Sync with work order data
    setWorkOrderData((prev) => ({
      ...prev,
      customerIdentity: customer.identity,
    }));
    // Clear unit when customer changes
    setSelectedUnit(null);
    setUnitSearchTerm("");
    setUnits([]);

    // If there's a linked work order, update the customer in InvoiceHeader
    if (item?.workOrderId && customer.identity) {
      try {
        await ScheduleItemAPI.updateWorkOrderCustomer({
          workOrderId: item.workOrderId,
          customerId: customer.identity,
          locationId: locationId,
          modifiedBy: userName,
          modifiedDate: toLocalISOString(new Date()),
        });
        //enqueueSnackbar("Customer updated in work order", { variant: "success" });
      } catch (error: any) {
        const errorMessage =
          error?.message || "Failed to update customer in work order";
        //enqueueSnackbar(errorMessage, { variant: "error" });
      }
    }
  };

  // Handle opening customer dialog for Add/Edit
  const handleOpenCustomerDialog = (mode: "create" | "edit") => {
    setCustomerDialogMode(mode);
    setCustomerDialogOpen(true);
  };

  // Handle customer dialog success - auto-bind newly created/updated customer
  const handleCustomerDialogSuccess = async (newCustomerId: number) => {
    // Close the customer dialog first
    setCustomerDialogOpen(false);
    
    // Note: NewCustomerDialog already shows success snackbar, so we don't show another one
    
    if (!newCustomerId) {
      console.error("handleCustomerDialogSuccess: No customer ID provided");
      return;
    }
    
    try {
      // Fetch customer details using getCustomerDetails API
      const customerDetails = await CustomerAPI.getCustomerDetails(newCustomerId);
      
      if (customerDetails && customerDetails.customerId) {
        // Bind the customer to the schedule item
        const custId = customerDetails.customerId;
        const custName = customerDetails.customerName || '';
        const custNumber = customerDetails.customerNumber || '';
        
        // Set state values for customer binding
        setCustomerId(custId);
        setCustomerName(custName);
        
        // Set the search term to show customer name and number
        const searchDisplay = custNumber ? `${custName} / ${custNumber}` : custName;
        setCustomerSearchTerm(searchDisplay.trim());
        
        // Also update work order data
        setWorkOrderData((prev) => ({
          ...prev,
          customerIdentity: custId,
        }));
        
        // Clear any previous search results and close dropdown
        setCustomers([]);
        setShowCustomerDropdown(false);
      } else {
        // Fallback: just set the customer ID so the user knows something was selected
        setCustomerId(newCustomerId);
        setWorkOrderData((prev) => ({
          ...prev,
          customerIdentity: newCustomerId,
        }));
        // The customer name field will be empty but at least we have the ID
        console.warn("getCustomerDetails returned null or no customerId, binding only ID:", newCustomerId);
      }
    } catch (error) {
      console.error("Failed to load customer details:", error);
      // Fallback: at least bind the customer ID
      setCustomerId(newCustomerId);
      setWorkOrderData((prev) => ({
        ...prev,
        customerIdentity: newCustomerId,
      }));
    }
  };

  // Handle click outside unit search dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        unitSearchRef.current &&
        !unitSearchRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
      }
    };

    if (showUnitDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUnitDropdown]);

  const fetchScheduleUnits = async (
    searchTerm: string,
    locId: number,
    custId: number | null,
    pageNumber = 1,
    append = false,
  ) => {
    if (!locId) {
      setUnits([]);
      setShowUnitDropdown(false);
      setUnitHasMore(false);
      return;
    }

    if (append) {
      setUnitLoadingMore(true);
    } else {
      setUnitSearchLoading(true);
      unitSearchPageRef.current = 1;
    }

    unitSearchContextRef.current = { searchTerm, locId, custId };

    try {
      const result = await UnitsAPI.GetUnitBySearchTerm(
        searchTerm,
        locId,
        custId || undefined,
        undefined,
        pageNumber,
        UNIT_SEARCH_PAGE_SIZE,
      );

      if (result) {
        setUnits((prev) =>
          append ? [...prev, ...result.units] : result.units,
        );
        setUnitHasMore(result.hasMore);
        unitSearchPageRef.current = pageNumber;
        setShowUnitDropdown(result.units.length > 0 || append);
      } else {
        if (!append) {
          setUnits([]);
          setShowUnitDropdown(false);
        }
        setUnitHasMore(false);
      }
    } catch (error) {
      console.error("Error searching units:", error);
      if (!append) {
        setUnits([]);
      }
      setUnitHasMore(false);
    } finally {
      if (append) {
        setUnitLoadingMore(false);
      } else {
        setUnitSearchLoading(false);
      }
    }
  };

  const loadMoreScheduleUnits = () => {
    const context = unitSearchContextRef.current;
    if (
      !context ||
      !unitHasMore ||
      unitLoadingMore ||
      unitSearchLoading
    ) {
      return;
    }

    fetchScheduleUnits(
      context.searchTerm,
      context.locId,
      context.custId,
      unitSearchPageRef.current + 1,
      true,
    );
  };

  const handleScheduleUnitListScroll = (
    event: React.UIEvent<HTMLUListElement>,
  ) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 12) {
      loadMoreScheduleUnits();
    }
  };

  // Debounced unit search - allows searching without customer (customer is optional)
  const debouncedSearchUnits = useMemo(
    () =>
      debounce(async (searchTerm: string, locId: number, custId: number | null) => {
        await fetchScheduleUnits(searchTerm, locId, custId);
      }, 300),
    [],
  );

  // Handle unit search input change
  const handleUnitSearchChange = (value: string) => {
    setUnitSearchTerm(value);
    setSelectedUnit(null);
    // Pass customerId if available, otherwise null
    debouncedSearchUnits(value, locationId, customerId || null);
  };

  // Handle unit selection - auto-populate customer if unit has currentCustomerId
  const handleUnitSelect = async (unit: Unit) => {
    setSelectedUnit(unit);
    setUnitSearchTerm(
      unit.baseSerial
        ? `${unit.unitNumber} - ${unit.baseSerial}`
        : unit.unitNumber,
    );
    setShowUnitDropdown(false);
    setUnits([]);
    setUnitHasMore(false);
    unitSearchPageRef.current = 1;
    unitSearchContextRef.current = null;

    // Auto-populate customer if unit has currentCustomerId and no customer is selected
    if (unit.currentCustomerId && !customerId) {
      try {
        const customerDetails = await CustomerAPI.getCustomerDetails(unit.currentCustomerId);
        if (customerDetails && customerDetails.customerId) {
          setCustomerId(customerDetails.customerId);
          setCustomerName(customerDetails.customerName || '');
          const custNumber = customerDetails.customerNumber || '';
          setCustomerSearchTerm((custNumber ? `${customerDetails.customerName} / ${custNumber}` : customerDetails.customerName || '').trim());
          
          // Update work order data
          setWorkOrderData((prev) => ({
            ...prev,
            customerIdentity: customerDetails.customerId,
          }));
          
          enqueueSnackbar("Customer auto-populated from selected unit", { variant: "info" });
        }
      } catch (error) {
        console.error("Failed to fetch customer details from unit:", error);
      }
    }

    // If there's a linked work order, update the unit in InvoiceHeader
    if (item?.workOrderId && unit.identity) {
      try {
        await ScheduleItemAPI.updateWorkOrderUnit({
          workOrderId: item.workOrderId,
          unitId: unit.identity,
          modifiedBy: userName,
          modifiedDate: toLocalISOString(new Date()),
        });
       // enqueueSnackbar("Unit updated in work order", { variant: "success" });
      } catch (error: any) {
        const errorMessage =
          error?.message || "Failed to update unit in work order";
        //enqueueSnackbar(errorMessage, { variant: "error" });
      }
    }
  };

  // Handle work order field changes
  const handleWorkOrderChange = (
    field: keyof WorkOrderFormData,
    value: number | string | undefined,
  ) => {
    setWorkOrderData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Navigate to Technician Calendar V2
  const handleNavigateToTechCalendar = () => {
    if (!workOrderData.technicianIdentity) return;
    
    const params = new URLSearchParams();
    params.set('technicianId', workOrderData.technicianIdentity.toString());
    // Use the schedule item's start date for the calendar view
    const dateStr = startDateTime.toISOString().split('T')[0];
    params.set('date', dateStr);
    navigate(`/TechnicianCalendar?${params.toString()}`);
  };

  // Segment management functions
  const handleAddSegment = () => {
    const newSegment = createEmptySegment();
    setWorkOrderData((prev) => ({
      ...prev,
      segments: [...prev.segments, newSegment],
    }));
  };

  const handleRemoveSegment = (segmentId: string) => {
    setWorkOrderData((prev) => ({
      ...prev,
      segments: prev.segments.filter((s) => s.id !== segmentId),
    }));
  };

  const handleSegmentChange = (
    segmentId: string,
    field: keyof SegmentFormData,
    value: string | number
  ) => {
    setWorkOrderData((prev) => ({
      ...prev,
      segments: prev.segments.map((s) =>
        s.id === segmentId ? { ...s, [field]: value } : s
      ),
      // Keep single segment fields in sync with first segment for backward compat
      ...(prev.segments.findIndex((s) => s.id === segmentId) === 0 &&
      (field === "segmentTitle" || field === "serviceCodeIdentity")
        ? { [field]: value }
        : {}),
    }));
  };

  const handleStartDateTimeChange = (value: string) => {
    setStartDateTimeString(value);
    const newDate = fromDateTimeLocalString(value);
    setStartDateTime(newDate);

    // If end time is before start time, update it to be 1 hour after
    if (newDate >= endDateTime) {
      const newEnd = new Date(newDate);
      newEnd.setMinutes(newEnd.getMinutes() + 60);
      setEndDateTime(newEnd);
      setEndDateTimeString(toDateTimeLocalString(newEnd));
    }
  };

  const handleEndDateTimeChange = (value: string) => {
    setEndDateTimeString(value);
    setEndDateTime(fromDateTimeLocalString(value));
  };

  const handleSave = async () => {
    if (!startDateTime || !endDateTime || customerId === 0) return;

    try {
      if (isEditing && item) {
        const updateRequest: UpdateScheduleItemRequest = {
          scheduleItemId: item.scheduleItemId,
          customerId,
          locationId,
          unitId: selectedUnit?.identity,
          startDateTime: toLocalISOString(startDateTime),
          endDateTime: toLocalISOString(endDateTime),
          title,
          description,
          status,
          timeZoneId,
          modifiedBy: currentUserId,
          rowVersion: item.rowVersion,
        };
        await onSave(updateRequest);

        // If there's an associated work order, sync customer/location/unit info
        if (item.workOrderId) {
          const now = toLocalISOString(new Date());

          // Update customer and location in work order
          try {
            await ScheduleItemAPI.updateWorkOrderCustomer({
              workOrderId: item.workOrderId,
              customerId: customerId,
              locationId: locationId,
              modifiedBy: userName,
              modifiedDate: now,
            });
          } catch (error) {
            console.error("Failed to update customer in work order:", error);
          }

          // Update unit in work order if unit is selected
          if (selectedUnit?.identity) {
            try {
              await ScheduleItemAPI.updateWorkOrderUnit({
                workOrderId: item.workOrderId,
                unitId: selectedUnit.identity,
                modifiedBy: userName,
                modifiedDate: now,
              });
            } catch (error) {
              console.error("Failed to update unit in work order:", error);
            }
          }
        }

        enqueueSnackbar("Customer Schedule updated successfully", {
          variant: "success",
        });
      } else {
        const createRequest: CreateScheduleItemRequest = {
          customerId,
          locationId,
          unitId: selectedUnit?.identity,
          callInDateTime: toLocalISOString(
            callInDateTime ?? fromDateTimeLocalString(callInDateTimeString),
          ),
          startDateTime: toLocalISOString(startDateTime),
          endDateTime: toLocalISOString(endDateTime),
          title,
          description,
          timeZoneId,
          createdBy: currentUserId,
        };
        await onSave(createRequest);
        enqueueSnackbar("Customer Schedule successfully saved", {
          variant: "success",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save customer schedule";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleCreateWorkOrder = (
    actionType: "draft" | "commit" | "save" | "archive",
  ) => {
    if (onCreateWorkOrder && customerId > 0 && item) {
      const workOrderRequest: WorkOrderFormData = {
        ...workOrderData,
        customerIdentity: customerId,
        locationIdentity: locationId,
        unitIdentity: selectedUnit?.identity,
        actionType,
        workOrderId: item.workOrderId,
        workOrderNumber: item.workOrderNumber,
        scheduleItemId: item.scheduleItemId,
        segmentId: item.segmentId,
      };
      onCreateWorkOrder(workOrderRequest);
    }
  };

  const handleDelete = () => {
    if (item && onDelete) {
      onDelete(item.scheduleItemId);
    }
  };

  const isValid =
    title.trim() &&
    customerId > 0 &&
    startDateTime &&
    endDateTime &&
    locationId > 0 &&
    endDateTime > startDateTime;

  const isWorkOrderValid =
    workOrderData.customerIdentity > 0 &&
    workOrderData.locationIdentity > 0 &&
    workOrderData.woStatusIdentity > 0 &&
    workOrderData.woPriorityIdentity > 0 &&
    workOrderData.salesPersonIdentity > 0 &&
    workOrderData.segmentTitle.trim().length > 0 &&
    workOrderData.segmentTitle.trim().length <= 15 &&
    workOrderData.serviceCodeIdentity > 0;

  const hasLinkedWorkOrder = Boolean(item?.workOrderId || item?.workOrderNumber);
  const isDraftWorkOrder = item?.woStatus?.toLowerCase() === "draft";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 650,
          height: 650,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <EventIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditing ? "Edit Customer Schedule" : "New Customer Schedule"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="appointment tabs"
        >
          <Tab
            icon={<EventIcon fontSize="small" />}
            iconPosition="start"
            label="Customer"
            sx={{ textTransform: "capitalize" }}
          />
          {onCreateWorkOrder && isEditing && (
            <Tab
              icon={<AssignmentIcon fontSize="small" />}
              iconPosition="start"
              sx={{ textTransform: "capitalize" }}
              label="Work Order"
            />
          )}
          {/* {isEditing && (
            <Tab
              icon={<InfoIcon fontSize="small" />}
              iconPosition="start"
              label="Audit Info"
            />
          )} */}
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, flex: 1, overflow: "auto" }}>
        {/* Tab 1: Details */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Title */}
            <TextField
              label="Title"
              value={title}
              size="small"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => markFieldTouched("title")}
              fullWidth
              required
              error={touchedFields.title && !title.trim()}
              helperText={
                touchedFields.title && !title.trim() ? "Title is required" : ""
              }
            />

            {/* Location - using all locations from store */}
            <FormControl fullWidth required>
              <InputLabel size="small">Location</InputLabel>
              <Select
                value={locationId}
                size="small"
                onChange={(e) => {
                  const newLocationId = Number(e.target.value);
                  setLocationId(newLocationId);
                  // Sync with work order data
                  setWorkOrderData((prev) => ({
                    ...prev,
                    locationIdentity: newLocationId,
                  }));
                  // Clear unit when location changes
                  setSelectedUnit(null);
                  setUnitSearchTerm("");
                  setUnits([]);
                }}
                label="Location"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {woLocations.map((loc) => (
                  <MenuItem key={loc.identity} value={loc.identity}>
                    {loc.displayText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Customer - With Search */}
            <Box sx={{ position: "relative" }} ref={customerSearchRef}>
              <TextField
                value={customerSearchTerm}
                size="small"
                onChange={(e) => handleCustomerSearchChange(e.target.value)}
                onFocus={() => {
                  if (customers.length > 0) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onBlur={() => markFieldTouched("customerId")}
                label={<RequiredLabel label="Customer" />}
                error={
                  (touchedFields.customerId && customerId === 0) ||
                  (customerSearchTerm.trim().length > 0 && customerId === 0)
                }
                helperText={
                  customerSearchTerm.trim().length > 0 && customerId === 0
                    ? "Please select a customer from the list"
                    : touchedFields.customerId && customerId === 0
                      ? "Customer is required"
                      : ""
                }
                fullWidth
                placeholder="Search customer..."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {customerSearchLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon sx={{ color: "action.disabled" }} />
                        )}
                        {/* Add/Edit Customer Button */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleOpenCustomerDialog(customerId > 0 ? "edit" : "create")
                          }
                          title={customerId > 0 ? "Edit Customer" : "Add Customer"}
                          sx={(theme) => ({
                            color: theme.palette.text.secondary, // default color
                            cursor: "pointer",
                            transition: "color 0.3s ease",
                            "&:hover": {
                              color: theme.palette.primary.main, // color on hover
                            },
                            // color on
                          })}
                        >
                          {customerId > 0 ? (
                            <EditIcon fontSize="small" />
                          ) : (
                            <AddCircleIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />
              {showCustomerDropdown && customers.length > 0 && (
                <List sx={searchCustomerListStyleTechnicianCalendar(theme)}>
                  {customers.map((customer) => (
                    <ListItem
                      sx={searchListItem(theme)}
                      key={customer.identity}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      {customer.name} / {customer.number}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Unit - Search with dropdown (customer optional, location required) */}
            <Box sx={{ position: "relative" }} ref={unitSearchRef}>
              <TextField
                label="Unit"
                value={unitSearchTerm}
                size="small"
                onChange={(e) => handleUnitSearchChange(e.target.value)}
                onFocus={() => {
                  if (locationId && units.length > 0) {
                    setShowUnitDropdown(true);
                  }
                }}
                fullWidth
                disabled={!locationId}
                placeholder={
                  !locationId
                    ? "Select location first"
                    : "Search unit..."
                }
                error={unitSearchTerm.trim().length > 0 && !selectedUnit}
                helperText={
                  unitSearchTerm.trim().length > 0 && !selectedUnit
                    ? "Please select a unit from the list"
                    : ""
                }
                InputProps={{
                  endAdornment: unitSearchLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon />
                  ),
                }}
              />
              {showUnitDropdown && units.length > 0 && (
                <List
                  onScroll={handleScheduleUnitListScroll}
                  sx={searchUnitListStyle(theme)}
                >
                  {units.map((unit) => (
                    <ListItem
                      sx={searchListItem(theme)}
                      key={unit.identity}
                      onClick={() => handleUnitSelect(unit)}
                    >
                      {unit.baseSerial
                        ? `${unit.unitNumber} - ${unit.baseSerial}${
                            unit.stockstatus?.trim() === "customer"
                              ? ` - ${unit.stockstatus}`
                              : ""
                          }`
                        : `${unit.unitNumber}${
                            unit.stockstatus?.trim() === "customer"
                              ? ` - ${unit.stockstatus}`
                              : ""
                          }`}
                    </ListItem>
                  ))}
                  {unitLoadingMore && (
                    <ListItem sx={{ justifyContent: "center", py: 1 }}>
                      <CircularProgress size={18} />
                    </ListItem>
                  )}
                </List>
              )}
            </Box>

            {/* Call In Date/Time (informational, set at creation) */}
            <TextField
              label="Call in Time"
              type="datetime-local"
              value={callInDateTimeString}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              helperText={
                isEditing
                  ? "Recorded when this schedule was created"
                  : "Set automatically when the schedule is saved"
              }
              inputProps={{
                step: 300,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  cursor: "default",
                },
              }}
            />

            {/* Start Date/Time */}
            <TextField
              label="Start Time"
              type="datetime-local"
              value={startDateTimeString}
              onChange={(e) => handleStartDateTimeChange(e.target.value)}
              fullWidth
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              error={!startDateTimeString}
              helperText={!startDateTimeString ? "Start time is required" : ""}
              inputProps={{
                step: 300, // 5 minute intervals
              }}
            />

            {/* End Date/Time */}
            <TextField
              label="End Time"
              type="datetime-local"
              value={endDateTimeString}
              onChange={(e) => handleEndDateTimeChange(e.target.value)}
              fullWidth
              required
              size="small"
              InputLabelProps={{ shrink: true }}
              error={!endDateTimeString || endDateTime <= startDateTime}
              helperText={
                !endDateTimeString
                  ? "End time is required"
                  : endDateTime <= startDateTime
                    ? "End time must be after start time"
                    : ""
              }
              inputProps={{
                step: 300, // 5 minute intervals
                min: startDateTimeString, // Prevent selecting before start
              }}
            />

            {/* Duration display */}
            {startDateTime && endDateTime && endDateTime > startDateTime && (
              <Paper variant="outlined" sx={{ p: 1, bgcolor: "grey.50" }}>
                <Typography variant="caption" color="text.secondary">
                  Duration:{" "}
                  {Math.round(
                    (endDateTime.getTime() - startDateTime.getTime()) /
                      (1000 * 60),
                  )}{" "}
                  minutes
                </Typography>
              </Paper>
            )}

            {/* Status (only for edit mode) */}
            {isEditing && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as (typeof statusOptions)[number])
                  }
                  label="Status"
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </TabPanel>

        {/* Tab 2: Work Order (only available when editing an existing schedule item) */}
        {onCreateWorkOrder && isEditing && (
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Work Order Information Section */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: theme.palette.primary.main,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Work Order Information
                  </Typography>
                  {item?.workOrderNumber && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.contrastText,
                          bgcolor: "rgba(255,255,255,0.2)",
                          px: 1.5,
                          py: 0.25,
                          borderRadius: 1,
                        }}
                      >
                        {item.workOrderNumber}
                      </Typography>
                      {item.woStatus && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "white",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            textTransform: "capitalize",
                          }}
                        >
                          {item.woStatus}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {/* Status */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={
                        touchedFields.woStatusIdentity &&
                        workOrderData.woStatusIdentity === 0
                      }
                    >
                      <InputLabel>
                        <RequiredLabel label="Status" />
                      </InputLabel>
                      <Select
                        value={workOrderData.woStatusIdentity || ""}
                        onChange={(e) =>
                          handleWorkOrderChange(
                            "woStatusIdentity",
                            Number(e.target.value) || 0,
                          )
                        }
                        onBlur={() => markFieldTouched("woStatusIdentity")}
                        label="Status"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {statusOptionsWO.map((status) => (
                          <MenuItem
                            key={status.identity}
                            value={status.identity}
                          >
                            {status.displayText}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Priority */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={
                        touchedFields.woPriorityIdentity &&
                        workOrderData.woPriorityIdentity === 0
                      }
                    >
                      <InputLabel>
                        <RequiredLabel label="Priority" />
                      </InputLabel>
                      <Select
                        value={workOrderData.woPriorityIdentity || ""}
                        onChange={(e) =>
                          handleWorkOrderChange(
                            "woPriorityIdentity",
                            Number(e.target.value) || 0,
                          )
                        }
                        onBlur={() => markFieldTouched("woPriorityIdentity")}
                        label="Priority"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {priorityOptions.map((priority) => (
                          <MenuItem
                            key={priority.identity}
                            value={priority.identity}
                          >
                            {priority.displayText}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Sales Person */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={salesPersonOptions}
                      size="small"
                      getOptionLabel={(option) => option.displayText}
                      value={
                        salesPersonOptions.find(
                          (sp) =>
                            sp.identity === workOrderData.salesPersonIdentity,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        handleWorkOrderChange(
                          "salesPersonIdentity",
                          newValue?.identity || 0,
                        );
                      }}
                      onBlur={() => markFieldTouched("salesPersonIdentity")}
                      ListboxProps={{
                        style: {
                          maxHeight: 150,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<RequiredLabel label="Sales Person" />}
                          error={
                            touchedFields.salesPersonIdentity &&
                            workOrderData.salesPersonIdentity === 0
                          }
                          helperText={
                            touchedFields.salesPersonIdentity &&
                            workOrderData.salesPersonIdentity === 0
                              ? "Sales person is required"
                              : ""
                          }
                          size="small"
                        />
                      )}
                    />
                  </Grid>

                  {/* Technician (Optional) with Tech Calendar Icon */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Autocomplete
                        options={technicianOptions}
                        size="small"
                        sx={{ flex: 1 }}
                        getOptionLabel={(option) => option.displayText}
                        value={
                          technicianOptions.find(
                            (t) =>
                              t.identity === workOrderData.technicianIdentity,
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          handleWorkOrderChange(
                            "technicianIdentity",
                            newValue?.identity,
                          );
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: 150,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Technician"
                            size="small"
                            placeholder="Optional"
                          />
                        )}
                      />
                      {/* Tech Calendar V2 Navigation - disabled if no technician or work order not saved */}
                      <Tooltip title={
                        !item?.workOrderId 
                          ? "Save work order first to view in Tech Calendar" 
                          : !workOrderData.technicianIdentity 
                            ? "Select a technician first" 
                            : "View in Technician Calendar V2"
                      }>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleNavigateToTechCalendar}
                            disabled={!workOrderData.technicianIdentity || !item?.workOrderId}
                            sx={{
                              color: workOrderData.technicianIdentity && item?.workOrderId
                                ? theme.palette.primary.main
                                : theme.palette.action.disabled,
                              border: `1px solid ${workOrderData.technicianIdentity && item?.workOrderId ? theme.palette.primary.main : theme.palette.action.disabled}`,
                              borderRadius: 1,
                              "&:hover": {
                                bgcolor: "primary.light",
                                color: "primary.contrastText",
                              },
                            }}
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Segment Information Section - Multi-segment support */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.contrastText,
                      bgcolor: theme.palette.primary.main,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                    }}
                  >
                    Segments ({workOrderData.segments.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSegment}
                    sx={{ textTransform: "capitalize" }}
                  >
                    Add Segment
                  </Button>
                </Box>

                {/* Segment List */}
                <Stack spacing={2}>
                  {workOrderData.segments.map((segment, index) => (
                    <Paper
                      key={segment.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        position: "relative",
                      }}
                    >
                      {/* Remove button - only show if more than one segment */}
                      {workOrderData.segments.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSegment(segment.id)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "error.main",
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}
                      >
                        Segment {index + 1}
                      </Typography>

                      <Grid container spacing={2}>
                        {/* Segment Title */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={<RequiredLabel label="Segment Title" />}
                            value={segment.segmentTitle}
                            onChange={(e) =>
                              handleSegmentChange(
                                segment.id,
                                "segmentTitle",
                                e.target.value.slice(0, 15)
                              )
                            }
                            onBlur={() => markFieldTouched(`segmentTitle_${segment.id}`)}
                            error={
                              touchedFields[`segmentTitle_${segment.id}`] &&
                              segment.segmentTitle.trim().length === 0
                            }
                            helperText={
                              touchedFields[`segmentTitle_${segment.id}`] &&
                              segment.segmentTitle.trim().length === 0
                                ? "Segment title is required"
                                : ""
                            }
                            inputProps={{ maxLength: 15 }}
                          />
                        </Grid>

                        {/* Service Code */}
                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            size="small"
                            error={
                              touchedFields[`serviceCodeIdentity_${segment.id}`] &&
                              segment.serviceCodeIdentity === 0
                            }
                          >
                            <InputLabel>
                              <RequiredLabel label="Service Code" />
                            </InputLabel>
                            <Select
                              value={segment.serviceCodeIdentity || ""}
                              onChange={(e) =>
                                handleSegmentChange(
                                  segment.id,
                                  "serviceCodeIdentity",
                                  Number(e.target.value) || 0
                                )
                              }
                              onBlur={() => markFieldTouched(`serviceCodeIdentity_${segment.id}`)}
                              label="Service Code"
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 150,
                                    maxWidth: 250,
                                  },
                                },
                              }}
                            >
                              {serviceCodeOptions.map((code) => (
                                <MenuItem
                                  key={code.identity}
                                  value={code.identity}
                                  sx={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {code.displayText}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Bill As */}
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Bill As</InputLabel>
                            <Select
                              value={segment.billAs}
                              onChange={(e) =>
                                handleSegmentChange(
                                  segment.id,
                                  "billAs",
                                  e.target.value as "actual" | "flatrate"
                                )
                              }
                              label="Bill As"
                            >
                              {billAsOptions.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Estimated Hours */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={
                              segment.billAs === "flatrate"
                                ? "Flat Rate Labor Hours"
                                : "Estimated Labor Hours"
                            }
                            value={
                              focusedHoursSegmentId === segment.id &&
                              segment.estimatedHours === 0
                                ? ""
                                : segment.estimatedHours
                            }
                            onFocus={() =>
                              setFocusedHoursSegmentId(segment.id)
                            }
                            onBlur={() => setFocusedHoursSegmentId(null)}
                            onChange={(e) =>
                              handleSegmentChange(
                                segment.id,
                                "estimatedHours",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            inputProps={{ min: 0, step: 0.5 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Box>
          </TabPanel>
        )}

        {/* Tab 3: Audit Info */}
        {isEditing && item && (
          <TabPanel value={activeTab} index={onCreateWorkOrder ? 2 : 1}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}
                >
                  Created
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>By:</strong> {item.createdByName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>On:</strong> {formatDate(item.createdDate, "long")}
                  </Typography>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}
                >
                  Last Modified
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>By:</strong> {item.modifiedByName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>On:</strong> {formatDate(item.modifiedDate, "long")}
                  </Typography>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}
                >
                  System Information
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>Schedule Item ID:</strong> {item.scheduleItemId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time Zone:</strong> {item.timeZoneId}
                  </Typography>
                  {item.workOrderId && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Linked Work Order:</strong>{" "}
                      {item.workOrderNumber || item.workOrderId}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </TabPanel>
        )}
      </DialogContent>

      {/* Actions - different buttons based on active tab */}
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3 },
          pb: 2,
          gap: { xs: 0.5, sm: 1 },
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {/* Work Order Tab Actions */}
        {activeTab === 1 && onCreateWorkOrder ? (
          <>
            {/* Left side - Remove button (only for existing work orders) */}
            {hasLinkedWorkOrder && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleCreateWorkOrder("archive")}
                disabled={isWorkOrderOperationPending}
                sx={{ textTransform: "capitalize", mr: "auto" }}
              >
                Archive
              </Button>
            )}
            {!hasLinkedWorkOrder && <Box sx={{ mr: "auto" }} />}
            
            {/* Cancel button */}
            <Button
              onClick={onClose}
              variant="outlined"
              color="inherit"
              size="small"
              disabled={isWorkOrderOperationPending}
              sx={{ textTransform: "capitalize", minWidth: { xs: "auto", sm: 80 } }}
            >
              Cancel
            </Button>

            {/* Right side - Work Order action buttons */}
            {hasLinkedWorkOrder ? (
              // Existing work order:
              //   - If committed: only Save Changes (no Draft or Commit button)
              //   - If draft: Save Changes + Commit
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCreateWorkOrder("save")}
                  disabled={!isWorkOrderValid || isWorkOrderOperationPending}
                  sx={{ 
                    textTransform: "capitalize",
                    minWidth: { xs: "auto", sm: 100 },
                    whiteSpace: "nowrap",
                  }}
                >
                  Save Changes
                </Button>
                {isDraftWorkOrder && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCreateWorkOrder("commit")}
                    disabled={!isWorkOrderValid || isWorkOrderOperationPending}
                    sx={{ textTransform: "capitalize", minWidth: { xs: "auto", sm: 80 } }}
                  >
                    Commit
                  </Button>
                )}
              </>
            ) : (
              // New work order (no WO linked yet):
              //   - Save Draft: creates WO in draft state; afterwards shows Save Changes + Commit
              //   - Commit: creates WO in committed state; afterwards shows only Save Changes
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCreateWorkOrder("draft")}
                  disabled={!isWorkOrderValid || isWorkOrderOperationPending}
                  sx={{ 
                    textTransform: "capitalize",
                    minWidth: { xs: "auto", sm: 90 },
                    whiteSpace: "nowrap",
                  }}
                >
                  Save Draft
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleCreateWorkOrder("commit")}
                  disabled={!isWorkOrderValid || isWorkOrderOperationPending}
                  sx={{ textTransform: "capitalize", minWidth: { xs: "auto", sm: 80 } }}
                >
                  Commit
                </Button>
              </>
            )}
          </>
        ) : (
          // Customer Tab or Audit Info Tab Actions
          <>
            {isEditing && onDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                sx={{ textTransform: "capitalize", mr: "auto" }}
              >
                Delete
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="outlined"
              color="inherit"
              size="small"
              sx={{ textTransform: "capitalize", minWidth: { xs: "auto", sm: 80 } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!isValid}
              sx={{ textTransform: "capitalize", minWidth: { xs: "auto", sm: 80 } }}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>

      {/* Customer Dialog for Add/Edit */}
      <NewCustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        customerId={customerDialogMode === "edit" ? customerId : undefined}
        locationIdentity={locationId}
        entBy={userName}
        mode={customerDialogMode}
        onCustomerCreated={handleCustomerDialogSuccess}
      />
    </Dialog>
  );
};
